import { isEqual } from "lodash";
import { BehaviorSubject, distinctUntilChanged, map, pairwise, startWith } from "rxjs";
import { UploaderState } from "../types/UploaderFile";

type State = UploaderState;

function getPatch(prev: State, next: State) {
  const nextKeys = Object.keys(next);
  const patch: Patch = {};
  nextKeys.forEach((k) => {
    if (prev[k] !== next[k]) patch[k] = next[k];
  });
  return patch;
}

export type Store = BehaviorSubject<Record<string, State>>;

type Options = {
  id: string;
  store: Store;
};

type Patch = Partial<State>;

type Callback = (prev: State, curr: State, patch: Patch) => unknown;

/**
 * Upload store.
 *
 * @param {object} opts.store - The store to use.
 * @param {string} opts.id - This store instance's ID or scope.
 */
export class UploadStore {
  private id;

  private store: Store;

  constructor(opts: Options) {
    this.store = opts.store;
    this.id = opts.id;

    // Calling `setState` to dispatch an action to the store.
    // The intent is to make sure that the reducer has run once.
    this.setState({});
  }

  setState(patch: Patch): void {
    const current = this.store.getValue();
    this.store.next({
      ...current,
      [this.id]: {
        ...current[this.id],
        ...patch,
      },
    });
  }

  getState(): State {
    return this.store.getValue()[this.id];
  }

  subscribe(callback: Callback): () => void {
    const subscription = this.store
      .pipe(
        map((store) => store[this.id]),
        startWith(this.getState()),
        distinctUntilChanged(isEqual),
        pairwise(),
      )
      .subscribe(([prevState, nextState]) =>
        callback(prevState, nextState, getPatch(prevState, nextState)),
      );

    return () => subscription.unsubscribe();
  }
}
